import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { typography, borders, textColors, surfaces } from "../../design-system/tokens";
import { fetchElementStylePresetPreviews } from "./panels/elementStylePresetsApi";
import {
  buildPresetFamilies,
  ElementStylePresetFamilyCard,
} from "./panels/ElementStylePresetFamilyCard";

const VIEWPORT_PADDING = 8;
const DROPDOWN_WIDTH_PX = 360;
const DROPDOWN_MAX_HEIGHT_PX = 520;

const InlineStylePresetPicker = ({
  elementType,
  onApply,
  triggerClassName = "",
  disabled = false,
  title = "Change style preset",
  children = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applyingPreset, setApplyingPreset] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["element-style-presets", elementType],
    queryFn: () => fetchElementStylePresetPreviews(elementType),
    enabled: isOpen && Boolean(elementType),
    staleTime: 5 * 60 * 1000,
  });

  const presets = useMemo(
    () => (Array.isArray(data?.presets) ? data.presets : []),
    [data?.presets],
  );

  const families = useMemo(() => buildPresetFamilies(presets), [presets]);

  const dropdownStyle = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        zIndex: 9999,
        top: `${VIEWPORT_PADDING}px`,
        right: `${VIEWPORT_PADDING}px`,
        width: `min(${DROPDOWN_WIDTH_PX}px, calc(100vw - 1rem))`,
        maxWidth: "calc(100vw - 1rem)",
      };
    }

    const rect = triggerRef.current?.getBoundingClientRect?.();
    if (!rect) {
      return {
        zIndex: 9999,
        top: `${VIEWPORT_PADDING}px`,
        right: `${VIEWPORT_PADDING}px`,
        width: `min(${DROPDOWN_WIDTH_PX}px, calc(100vw - 1rem))`,
        maxWidth: "calc(100vw - 1rem)",
      };
    }

    return {
      zIndex: 9999,
      top: `${Math.max(
        VIEWPORT_PADDING,
        Math.min(
          rect.bottom + 8,
          window.innerHeight - DROPDOWN_MAX_HEIGHT_PX - VIEWPORT_PADDING,
        ),
      )}px`,
      right: `${Math.max(VIEWPORT_PADDING, window.innerWidth - rect.right)}px`,
      width: `min(${DROPDOWN_WIDTH_PX}px, calc(100vw - 1rem))`,
      maxWidth: "calc(100vw - 1rem)",
    };
  }, [isOpen]);

  const handleApply = useCallback(
    async (presetValue) => {
      if (!presetValue || disabled || applyingPreset) return;

      try {
        setApplyError(null);
        setApplyingPreset(presetValue);
        await Promise.resolve(onApply?.(presetValue));
        setIsOpen(false);
      } catch (error) {
        setApplyError(error?.message || "Failed to apply style preset.");
      } finally {
        setApplyingPreset(null);
      }
    },
    [applyingPreset, disabled, onApply],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    const handleKey = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    // Use capture phase so we still detect outside clicks even when
    // sibling pickers/buttons (e.g. the font family picker trigger) call
    // event.stopPropagation() in their own handlers. Without capture, React's
    // SyntheticEvent.stopPropagation() also halts the native event before it
    // bubbles to document, leaving this dropdown stuck open.
    document.addEventListener("mousedown", handleClick, true);
    document.addEventListener("touchstart", handleClick, true);
    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("mousedown", handleClick, true);
      document.removeEventListener("touchstart", handleClick, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        title={title}
        aria-label={title}
        aria-expanded={isOpen}
        disabled={disabled}
        className={triggerClassName}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (disabled) return;
          setApplyError(null);
          setIsOpen((prev) => !prev);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {children}
        <span className="sr-only">{title}</span>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          data-no-dnd="true"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          className={`editor-theme fixed overflow-hidden rounded-lg shadow-2xl ${surfaces.elevated} ${borders.default}`}
          style={dropdownStyle}
        >
          {applyError ? (
            <div className="border-b border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/40 dark:bg-red-900/20">
              <div className={`${typography.caption} text-red-700 dark:text-red-300`}>
                {applyError}
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="space-y-2 p-3">
              <div className={`${typography.caption} text-red-700 dark:text-red-300`}>
                {error?.message || "Failed to load style presets."}
              </div>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  refetch();
                }}
                className={`
                  inline-flex w-full items-center justify-center rounded-md border px-3 py-2
                  ${typography.button} ${borders.default} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}
                `}
              >
                Try again
              </button>
            </div>
          ) : isLoading ? (
            <div className="flex items-center gap-2 px-3 py-3">
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600 dark:border-blue-400" />
              <span className={`${typography.caption} ${textColors.secondary}`}>
                Loading styles...
              </span>
            </div>
          ) : families.length === 0 ? (
            <div className={`px-3 py-3 ${typography.caption} ${textColors.secondary}`}>
              No style choices are available.
            </div>
          ) : (
            <div
              className="flex flex-col gap-4 overflow-y-auto p-3"
              style={{ maxHeight: `${DROPDOWN_MAX_HEIGHT_PX}px` }}
            >
              {families.map((family) => (
                <ElementStylePresetFamilyCard
                  key={family.key}
                  family={family}
                  elementType={elementType}
                  applyingPreset={applyingPreset}
                  onApply={handleApply}
                  scaleMultiplier={0.78}
                  verticalPaddingPx={28}
                  variantDotSize="sm"
                />
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
};

export default InlineStylePresetPicker;
